import {Add, Close, Fullscreen, FullscreenExit, KeyboardArrowLeftRounded, KeyboardArrowRightRounded, SortByAlphaRounded} from "@mui/icons-material";
import {Box, Button, Checkbox, Collapse, Divider, Grid, List, ListItem, ListItemButton, ListItemSecondaryAction, ListItemText, Popover, Radio, Stack, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, Typography} from "@mui/material";
import {ChangeEvent, Children, ReactNode, cloneElement, createRef, isValidElement, useEffect, useState} from "react";
import IconButtonPop from "./IconButtonPop";
import {useNavigate, useSearchParams} from "react-router-dom";
import useWindowDimensions, {camelCase, camelCaseToNormal, objectsEqualNew} from "../Util/util";
import Card from "./Card";
import {LoadingButton} from "./LoadingButton";
import InputGroup from "./FormComponents/InputGroup";
import {InfoTableCell} from "../Util/styledComponents";
import {useDataViewerPaginator} from "../Util/hooks";
import {DataViewerFilterView, DataViewerPaginater, FilterObject, QueryObject, Sorts} from "../Util/types";
import {useFilterSelector} from "./FilterSelector";
import FilterList from "./FilterList";
import {SearchBox} from "./SearchBox";
import {Popup} from "./Popup";

// editCols must be a link to the collection where to alter docs

type FilterTableParams = {
    title?: ReactNode,
    subtitle?: string,
    id?: string,
    data?: {[key:string]: {[key:string]: unknown}}|QueryObject[],
    columns?: string[],
    columnTemplate?: {[key:string]: unknown}, // Allow you to edit the value displayed. Can be a callback function.
    columnHeaders?: {[key: string]: ReactNode} // Allow you to change the header of a column from one value to another. Useful if data key is not user friendly.
    children?: React.ReactNode,
    setSelectedRows?: (e:unknown) => void,
    setTableFilters?: (e:{[key: string]: unknown; }) => void,
    formatQueryData?: (e:unknown) => void,
    onItemClick?: (id:string, row?: {[key:string]: unknown}) => void,
    filterNames?: FilterObject,
    access?: boolean,
    hook?: (e: DataViewerPaginater) => Partial<ReturnType<typeof useDataViewerPaginator>>,
    viewRows?: boolean,
    disableSelection?: boolean,
    onSearch?: boolean | ((search?: string, sort?: [string, {value: string, direction: "asc"|"desc"}], page?: number, filters?:FilterObject, limit?: number) => Promise<{ [key: string]: any }>);
    listViewRenderer?: (k: string, v: any) => Promise<(false | JSX.Element)>|JSX.Element,
    noResultsText?: ReactNode,
    actions?: ReactNode,
    viewType?: "list"|"table",
    limit?: number,
    snapshot?: boolean,
    additionalEntryProcessing?: (k: string, v: any) => Promise<any> | any,
    grid?: boolean,
    xs?: number,
    sm?: number,
    md?: number,
    lg?: number,
    sx?: {[key: string]: unknown},
    noFullscreen?: boolean,
    savedFilters?: {
        [key: string]: DataViewerFilterView
    },
    onSaveFilters?: (type: "me"|"everyone", view: DataViewerFilterView) => void,
    onDeleteSavedFilter?: (view: string) => Promise<void>
}

// NEED TO ADD DISABLESELECTION!!!!!!
// NEED TO ADD HIDENORESULTSTEXT
// NEED TO ADD SETSELECTEDROWS

export default function DataViewer({
    title, subtitle, onSearch, viewType = "list", snapshot, noResultsText, actions, listViewRenderer,
    data = {}, hook = (e) => useDataViewerPaginator(e), disableSelection, /* setTableFilters, */columnHeaders,
    columns, columnTemplate = {}, additionalEntryProcessing, children, setSelectedRows,
    formatQueryData, filterNames, id, grid, sx, xs, sm, md, lg, noFullscreen, /* viewRows, */onItemClick,
    access = true, limit, savedFilters, onSaveFilters, onDeleteSavedFilter,
}: FilterTableParams) {
    const [selected, setSelected] = useState<{ [key: string]: { [key: string]: unknown } }>();
    const [allSelected, setAllSelected] = useState(false);
    const [renderedItems, setRenderedItems] = useState<(false | JSX.Element)[]>();
    const [fullscreen, setFullscreen] = useState(false);
    const navigate = useNavigate();

    const [view, setView] = useState<"list" | "table">((columns && listViewRenderer) ? viewType : (!columns ? "list" : !listViewRenderer ? "table" : "list"));
    const {width} = useWindowDimensions();

    const [searchParams] = useSearchParams();
    const params = Object.fromEntries([...searchParams]);
    const selectedView = params.id === id ? params?.selectedView || undefined : undefined;
    const [saveView, setSaveView] = useState(false);
    const [viewTitle, setViewTitle] = useState<string>();
    const [deleteView, setDeleteView] = useState<string>();


    const {filters} = useGetFilters({urlRef: id || camelCase((title && typeof title === "string") ? title : "dataViewer"), filterNames});
    const {
        tableData,
        loading,
        page,
        pageUp,
        pageDown,
        setFilters,
        updateSearch,
        updateSort,
        setView: setHookView,
        sorts,
        sort,
        search,
    } = hook({data, view, queryLimit: limit, snapshot, additionalEntryProcessing, initialSearch: (selectedView && savedFilters) ? savedFilters[selectedView]?.search : undefined, initialSort: (selectedView && savedFilters) ? savedFilters[selectedView]?.sort : undefined, filters: (selectedView && savedFilters) ? savedFilters[selectedView]?.filters : undefined, onSearch});


    useEffect(() => {
        view && setHookView && setHookView(view);
    }, [view]);


    const updateSelectedView = (view: string) => {
        if (!view || !savedFilters || !savedFilters[view]) {
            navigate("?");
            updateSort && updateSort(undefined);
            setFilters && setFilters(undefined);
            return;
        }

        updateSort && updateSort(savedFilters?.[view]?.sort);
        setFilters && setFilters(savedFilters?.[view]?.filters);

        const query = Object.entries(savedFilters[view]?.filters || {}).reduce((acc, [name, item]) => {
            acc.push(`${name}=${item.value}`);
            return acc;
        }, [] as Array<string>);

        id && query.unshift(`id=${id}`);
        view && view !== "all" && query.unshift(`selectedView=${view}`);

        navigate(`?${query.join("&")}`);
    };

    useEffect(() => {
        setFilters && setFilters(filters);
    }, [filters]);

    useEffect(() => {
        if (!selectedView && Object.keys(savedFilters || {}).length > 0) {
            updateSelectedView(Object.keys(savedFilters || {})[0]);
        }
    }, []);

    const tableContainerScrollRef = createRef<HTMLDivElement>();

    const {ActiveFilters, OpenFiltersPopup, SelectFiltersPopover} = useFilterSelector({filters: filterNames, urlRef: id, selectedView: selectedView});
    /*
    useEffect(() => {
        setSelected(undefined);
        setAllSelected(false);
        setSelectedRows && setSelectedRows({});
        reset();
    }, [updateFilters]);
*/
    useEffect(() => {
        if (!tableContainerScrollRef.current) return;
        tableContainerScrollRef.current.scrollTo(0, 0);
    }, [tableData]);

    useEffect(() => {
        if (allSelected) {
            setSelectedRows && setSelectedRows("All");
            return;
        }
        if (setSelectedRows && selected) {
            setSelectedRows(selected);
        }
    }, [selected, setAllSelected]);

    const setAllSelectedHandler = () => {
        if (allSelected) {
            setSelected({});
            setAllSelected(false);
        } else {
            setSelected(tableData);
            setAllSelected(true);
        }
    };

    useEffect(() => {
        const renderPlacements = async () => {
            if (!tableData || !listViewRenderer) return;
            setRenderedItems(
                (await Promise.all(Object.entries(tableData).map(async ([key, item]) => await listViewRenderer(key, item)))).filter((x) => x)
            );
        };
        renderPlacements();
    }, [tableData, width, listViewRenderer]);

    const saveCustomView = async (type: "me"|"everyone") => {
        if (!onSaveFilters) throw new Error("Saving not permitted.");
        if (!viewTitle) throw new Error("Add a title before saving.");
        const views: DataViewerFilterView = {
            title: viewTitle,
        };
        if (sort) {
            views.sort = sort[0];
        }
        if (search) {
            views.search = search;
        }
        const possibleFilters = Object.fromEntries(Object.entries(filters || {}).filter(([, v]) => v.value));
        if (Object.keys(possibleFilters || {}).length) {
            views.filters = possibleFilters;
        }
        return await onSaveFilters(type, views);
    };


    const filtersWithValues = Object.entries(filters || {}).filter(([, v]) => v.value).map(([k, v]) => [k, v.value]).sort();
    const viewFiltersWithValues = Object.entries(savedFilters?.[selectedView || ""]?.filters || {}).filter(([, v]) => v.value).map(([k, v]) => [k, v.value]).sort();

    const saveNewFiltersPopup = (!selectedView && (Object.keys(filters || {}).length || sort)) || !objectsEqualNew({sort: sort ? sort[0] : undefined, filters: filtersWithValues}, {sort: savedFilters?.[selectedView || ""]?.sort, filters: viewFiltersWithValues});

    console.log("TABLE DATA", tableData);

    const dataTable = (view === "table" && columns) ? (
        <Box sx={{maxHeight: "100%", display: "flex", flexFlow: "column", position: "relative"}}>
            <TableContainer ref={tableContainerScrollRef} style={{flex: "1 1 auto", marginBottom: "24px"}}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            {disableSelection || <TableCell style={{padding: 0}}><Checkbox checked={allSelected} onClick={() => setAllSelectedHandler()}/></TableCell>}
                            {columns.map((value) => (
                                <TableCell style={{padding: 0}} key={value}>
                                    <ListItem disablePadding style={{paddingLeft: 0}}>
                                        <ListItemText primary={columnHeaders?.[value] || camelCaseToNormal(value) as any}/>
                                    </ListItem>
                                </TableCell>
                            ))}
                            {Object.entries(tableData || {}).find(([, v]) => (v as {[key:string]:unknown}).flags) && <TableCell style={{padding: 0}}></TableCell>}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {Object.entries((((formatQueryData) ? formatQueryData(tableData) : tableData) || {}) as {[key: string]: { [key: string]: unknown }}).map(([key, row]) => (
                            <TableRow key={key} onClick={() => onItemClick && onItemClick(key, row)} sx={onItemClick ? {":hover": {background: "#00000012"}, "cursor": "pointer"} : {}}>
                                {disableSelection || <TableCell style={{padding: 0}}>
                                    <Checkbox checked={Boolean(allSelected || (selected && Object.prototype.hasOwnProperty.call(selected, key)))}
                                        onClick={(e) => {
                                            selected && Object.prototype.hasOwnProperty.call(selected, key) ? setSelected((s) => s ? Object.fromEntries(Object.entries(s).filter(([k]) => k !== key)) : undefined) : setSelected({...selected, [key]: row});
                                            e.stopPropagation();
                                        }}/>
                                </TableCell>}
                                {columns.map((value) => {
                                    const colValue = (columnTemplate[value] && (columnTemplate[value] as (e:unknown, row: unknown) => string)(row[value], row)) || row[value] as string;

                                    return <TableCell>{colValue as string}</TableCell>;
                                })}
                            </TableRow>
                        ))}
                        {Object.keys(tableData || {}).length === 0 ?
                            <TableRow>
                                <TableCell colSpan={columns.length + 2} sx={{textAlign: "center", opacity: 0.7}}>
                                    {access ? loading === "loaded" ? "End of results" : "No data to display." : "You don't have access to this. Contact your administrator to update access in your user group."}
                                </TableCell>
                            </TableRow> : loading === "loaded" && <TableRow>
                                <TableCell colSpan={columns.length + 2} sx={{textAlign: "center", opacity: 0.7}}>
                                    End of results
                                </TableCell>
                            </TableRow>}
                    </TableBody>
                </Table>
            </TableContainer>
            <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"} mb={0}>
                <Typography flex={1}>Page {page}</Typography>
                <IconButtonPop title="Page down" responsive={false} onClick={pageDown} disabled={page === 1}><KeyboardArrowLeftRounded/></IconButtonPop>
                <IconButtonPop title="Page up" responsive={false} onClick={pageUp} disabled={loading === "loaded"}><KeyboardArrowRightRounded/></IconButtonPop>
            </Stack>
        </Box>
    ) : (listViewRenderer ?
        <FilterList
            loadMoreIcon={Object.entries(tableData || {}).length === 0 ? "loaded" : loading}
            card={false}
            noResultsText={noResultsText}
            data={tableData}
            noSearch
            onScrollBottom={pageUp}>
            {renderedItems}
        </FilterList> : "No view defined.");

    return (
        <>
            <Box sx={{opacity: fullscreen ? 1 : 0, transition: "opacity 225ms cubic-bezier(0.4, 0, 0.2, 1);", pointerEvents: fullscreen ? "all" : "none", cursor: "pointer"}} onClick={() => setFullscreen(false)} bgcolor={"rgba(0, 0, 0, 0.5)"} width={"100%"} height={"100%"} top={0} left={0} zIndex={10002} position={"fixed"}/>
            <Card
                title={<Box key={"title"} minWidth={"max-content"}>{title as any}</Box>}
                subtitle={subtitle}
                sx={{height: fullscreen ? "calc(100vh - 80px)" : "100%", position: fullscreen ? "fixed" : undefined, left: fullscreen ? 40 : undefined, top: fullscreen ? 40 : undefined, width: fullscreen ? "calc(100% - 80px)" : "100%", zIndex: fullscreen ? 12002 : undefined, ...(fullscreen ? {} : (sx || {}))}}
                contentSx={{display: "flex"}}
                {...{grid, xs, sm, md, lg}}
                secondaryTitle={
                    <Stack key={"search"} direction={"row"} alignItems={"center"} width={"max-content"} spacing={1}>
                        {onSearch && updateSearch && <SearchBox placeholder={"Search"} onSearch={updateSearch}/>}
                        {(columns && listViewRenderer) && <Button sx={{color: "#00000099", mr: "0px !important"}} variant="text" onClick={() => setView((v) => v === "list" ? "table" : "list")}>{view === "list" ? "Table" : "List"} view</Button>}
                        {sorts && updateSort && <SortSelector sorts={sorts} onSort={updateSort} sort={sort?.[0]}/>}
                        {filterNames && <>{OpenFiltersPopup}{SelectFiltersPopover}</>}
                        {actions as any}
                        {noFullscreen || <Box flex={1}><IconButtonPop onClick={() => setFullscreen((f) => !f)} sx={{alignSelf: "center", float: "right"}} title={fullscreen ? "Close fullscreen" : "Open in fullscreen"} responsive={false}>{fullscreen ? <FullscreenExit/> : <Fullscreen/>}</IconButtonPop></Box>}
                    </Stack>
                }>
                <Stack flex={1} mt={-1.5} spacing={0} maxWidth={"100%"}>
                    {filterNames &&
                    <Collapse in={ActiveFilters && ActiveFilters?.length > 0} sx={{transition: "all 150ms ease-in-out", marginBottom: ActiveFilters && ActiveFilters?.length > 0 ? "20px !important" : 0}}>
                        <Stack direction={"row"} alignItems={"center"} justifyContent={"flex-end"} spacing={1}>
                            {ActiveFilters && ActiveFilters?.length > 0 && <Typography fontSize={"0.875rem"} color={"rgba(0, 0, 0, 0.6)"}>Filters: </Typography>}
                            {ActiveFilters}
                        </Stack>
                    </Collapse>}
                    {((savedFilters && Object.keys(savedFilters).length) || saveNewFiltersPopup && onSaveFilters) && <>
                        {/* <Typography marginTop={"-20px !important"} fontSize={"0.875rem"} color={"rgba(0, 0, 0, 0.6)"} zIndex={100}>Saved Views</Typography> */}
                        <Stack mt={"-5px !important"} mb={"10px !important"} key={"savedFilters"} direction={"row"} justifyContent={"start"} alignItems={"center"} spacing={0}>
                            <Tabs sx={{flex: 1, margin: "0px !important", minHeight: "10px !important"}} value={selectedView} onChange={(e, v) => v !== "new" && updateSelectedView(v)} allowScrollButtonsMobile>
                                {(false && savedFilters && Object.keys(savedFilters || {}).length) && <Tab sx={{minHeight: "10px !important"}} key={"all"} value={"all"} label={"All data"}/>}
                                {Object.entries(savedFilters || {}).map(([k, sF]) => <Tab sx={{minHeight: "10px !important"}} key={k} value={k} label={sF.title} iconPosition="end" icon={(sF.permanent || k !== selectedView) ? undefined : <IconButtonPop responsive={false} title="Delete" sx={{padding: 0, margin: 0}} onClick={() => setDeleteView(k)}><Close fontSize="small"/></IconButtonPop>}/>)}
                                {onSaveFilters && <Tab sx={{minHeight: "10px !important", opacity: saveNewFiltersPopup ? 1 : 0, pointerEvents: saveNewFiltersPopup ? "all" : "none", transition: "opacity 500ms ease-in-out"}} onClick={() => setSaveView(true)} value={"new"} icon={<Add/>} iconPosition="start" label={"Save View"}/>}
                            </Tabs>
                        </Stack>
                    </>}
                    <Stack direction={"row"} alignItems={"center"}>
                        {Children.count(children) > 0 && (
                            <Box sx={{display: "inline", position: "relative", marginLeft: "8px"}}>
                                <Typography
                                    position={"absolute"}
                                    top={"50%"}
                                    sx={{translate: "0 -50%", width: "max-content", opacity: selected && Object.keys(selected).length ? 0 : 1, transition: "opacity 100ms ease-in-out"}}
                                    variant="subtitle2"
                                    color={"#0000006d"}
                                    display={"inline-block"}
                                >
                        Select an item to view options
                                </Typography>
                                {Children.map(children, (child) => {
                                    if (!isValidElement(child)) {
                                        return child;
                                    }
                                    const props = {sx: {opacity: (selected && Object.keys(selected).length) ? 1 : 0, transition: "opacity 100ms ease-in-out"}};
                                    return cloneElement(child, props);
                                }) as any}
                            </Box>
                        )}</Stack>
                    <Stack overflow={"auto"}>
                        {dataTable}
                    </Stack>
                </Stack>
                {onSaveFilters &&
                    <Popup open={saveView} maxWidth="sm" fullWidth onClose={() => setSaveView(false)} title={"Save custom filters"}>
                        <Stack>
                            <Grid container>
                                <Grid item xs={12}>
                                    <InputGroup name={"viewTitle"} required label={"View Title"} onChange={(e: ChangeEvent<HTMLInputElement>) => setViewTitle(e.target.value)}/>
                                </Grid>
                                {Object.entries(filters || {}).filter(([, v]) => v.value && (v.type === "dropdown" ? v.values : true)).length > 0 && <Grid item xs={12}>
                                    <Typography variant="h6">Filters</Typography>
                                    <Table>
                                        {Object.entries(filters || {}).filter(([, v]) => v.value && (v.type === "dropdown" ? v.values : true)).map(([, v]) => <TableRow><InfoTableCell>{v.label}</InfoTableCell><InfoTableCell>{v.type === "dropdown" ? (v.values as any)[v.value as string] as string : camelCaseToNormal(v.value as string || "")}</InfoTableCell></TableRow>)}
                                    </Table>
                                </Grid>}
                                {sort && <>
                                    <Grid item xs={12}>
                                        <Divider/>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="h6">Sort</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        {sort[0]}
                                    </Grid>
                                </>}
                                <Grid item xs={6}>
                                    <LoadingButton text="Save for me" onClick={async () => {
                                        return await saveCustomView("me");
                                    }}/>
                                </Grid>
                                <Grid item xs={6}>
                                    <LoadingButton text="Save for everyone" variant="contained" onClick={async () => {
                                        return await saveCustomView("everyone");
                                    }}/>
                                </Grid>
                            </Grid>
                        </Stack>
                    </Popup>}
                {onDeleteSavedFilter && <Popup open={Boolean(deleteView)} onClose={() => setDeleteView(undefined)} title={"Delete view"}>
                    <Stack>
                        <Typography>Are you sure you want to delete view: {savedFilters?.[deleteView as string]?.title}</Typography>
                        <LoadingButton text="Delete" onClick={async () => deleteView && await onDeleteSavedFilter(deleteView).then(() => updateSelectedView("all"))}/>
                    </Stack>
                </Popup>}
            </Card>
        </>);
}


const useGetFilters = ({urlRef, filterNames}:{urlRef?: string, filterNames?: FilterObject}) => {
    const [filters, setFilters] = useState<FilterObject>();

    const [searchParams] = useSearchParams();

    useEffect(() => {
        if (!urlRef || !filterNames) return;
        const f = Object.fromEntries([...searchParams]);
        const filterElementId = f.id;
        delete f.id;
        delete f.selectedView;

        if (filterElementId !== urlRef) {
            setFilters({});
            return;
        }

        const filtersWithValues = {...filterNames};

        Object.entries(f).map(([k, v]) => {
            const filterType = filtersWithValues[k].type;
            switch (filterType) {
            case "string":
                filtersWithValues[k].value = v;
                break;
            case "number":
                filtersWithValues[k].value = parseInt(v);
                break;
            case "dropdown":
                filtersWithValues[k].value = v === "true" ? true : v === "false" ? false : v;
                break;
            default:
                filtersWithValues[k].value = v;
                break;
            }
        });
        setFilters(filtersWithValues);
    }, [searchParams]);

    return {filters};
};

type SortSelectorParams = {
    sorts: Sorts,
    sort?: string,
    onSort: (sort: string) => void
}

function SortSelector({sorts, sort, onSort}: SortSelectorParams) {
    const [sortOpen, setSortOpen] = useState<Element>();

    const handleSortButtonClick = (event:React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (event.detail === 0) return;
        setSortOpen(event.currentTarget);
    };

    if (!sorts) return null;

    return (
        <Stack direction={"row"} alignItems={"center"} height={"min-content"}>
            <IconButtonPop sx={{color: "38383b"}} aria-describedby={"filterPopup"} responsive={false} title="Sort results" variant="text" onClick={(e) => handleSortButtonClick(e)}><SortByAlphaRounded fontSize="small"/></IconButtonPop>
            <Popover
                open={Boolean(sortOpen)}
                onClose={() => setSortOpen(undefined)}
                id={"filterPopup"}
                anchorEl={sortOpen}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
            >
                <Box sx={{minWidth: "300px"}}>
                    <Typography p={2} pb={0} fontWeight={"bold"} color={"primary"}>Sort results by</Typography>
                    <List>
                        {Object.keys(sorts).map((sortName, i) =>
                            <ListItemButton key={i} onClick={() => {
                                if (sort !== sortName) {
                                    onSort(sortName);
                                }
                                setSortOpen(undefined);
                            }}>
                                <ListItemText primary={sortName}/>
                                <ListItemSecondaryAction sx={{mr: 0}}>
                                    <Radio checked={sort === sortName}/>
                                </ListItemSecondaryAction>
                            </ListItemButton>
                        )}
                    </List>
                </Box>
            </Popover>
        </Stack>
    );
}
